import { Lightbulb, Tags } from 'lucide-react'
import { DocSection } from '@/components/docs/doc-section'
import { DocPageHeader } from '@/components/docs/doc-page-header'
import { DocNav } from '@/components/docs/doc-nav'
import { MermaidDiagram } from '@/components/docs/mermaid-diagram'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'
import {
  Card,
  CardHeader,
  CardDescription,
} from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

const categories = [
  {
    title: 'Subject',
    borderColor: 'border-blue-500',
    badgeClassName: 'bg-blue-100 text-blue-800',
    description:
      'Who is making the request? Examples: role, department, clearance level, job title',
  },
  {
    title: 'Resource',
    borderColor: 'border-green-500',
    badgeClassName: 'bg-green-100 text-green-800',
    description:
      'What is being accessed? Examples: table name, data sensitivity, resource owner',
  },
  {
    title: 'Action',
    borderColor: 'border-amber-500',
    badgeClassName: 'bg-amber-100 text-amber-800',
    description:
      'What operation is requested? Examples: SELECT, INSERT, UPDATE, DELETE',
  },
  {
    title: 'Environment',
    borderColor: 'border-purple-500',
    badgeClassName: 'bg-purple-100 text-purple-800',
    description:
      'What is the context? Examples: time of day, IP address, network location',
  },
] as const

const dataTypes = [
  { type: 'string', description: 'Text values', example: '"finance"' },
  { type: 'integer', description: 'Whole numbers', example: '3' },
  { type: 'boolean', description: 'True/false flags', example: 'true' },
  { type: 'date', description: 'Calendar dates', example: '2024-01-15' },
  { type: 'time', description: 'Time of day', example: '09:00:00' },
  { type: 'anyURI', description: 'Resource identifiers', example: '/db/employees' },
] as const

export function AttributesPage() {
  return (
    <>
      <DocPageHeader icon={Tags} title="Attributes" />

      <div className="flex flex-1 flex-col gap-6 overflow-auto p-6 max-w-4xl">
        <DocSection title="What are Attributes?">
          <p className="text-sm text-muted-foreground leading-relaxed">
            Attributes are named properties that describe the entities involved in an access
            control decision. Think of them as the vocabulary your policies use — they are
            key-value pairs like <code className="text-xs bg-muted px-1 py-0.5 rounded">department="finance"</code> or{' '}
            <code className="text-xs bg-muted px-1 py-0.5 rounded">sensitivity="high"</code>.
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Every XACML request is built from attributes. When the PDP evaluates a policy, it
            matches the attributes in the request against the conditions in your rules. The richer
            your attribute vocabulary, the more precise your policies can be. For example, instead
            of "only admins can see this table," you can write "anyone in the finance department
            with clearance level 3 or above can SELECT from the payroll table during business
            hours."
          </p>
        </DocSection>

        <Separator />

        <DocSection title="The Four Categories">
          <p className="text-sm text-muted-foreground leading-relaxed">
            XACML organizes attributes into four categories. Each category answers a different
            question about the access request:
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {categories.map((category) => (
              <Card key={category.title} className={`border-l-4 ${category.borderColor}`}>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Badge className={category.badgeClassName}>{category.title}</Badge>
                  </div>
                  <CardDescription className="text-xs leading-relaxed">
                    {category.description}
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </DocSection>

        <MermaidDiagram
          src="/docs/diagrams/attribute-categories.svg"
          alt="Attribute categories hierarchy showing Subject, Resource, Action, and Environment"
          caption="Attribute category hierarchy with example attributes"
        />

        <Separator />

        <DocSection title="Data Types">
          <p className="text-sm text-muted-foreground leading-relaxed">
            Each attribute has a data type that determines how the PDP compares values during
            evaluation. Choosing the right type ensures your policy conditions work correctly —
            for instance, comparing an integer clearance level with greater-than rather than
            string comparison.
          </p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Example</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dataTypes.map((row) => (
                <TableRow key={row.type}>
                  <TableCell className="font-mono text-xs">{row.type}</TableCell>
                  <TableCell>{row.description}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {row.example}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DocSection>

        <Separator />

        <DocSection title="Creating Attributes in poli-x">
          <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground leading-relaxed">
            <li>
              Navigate to{' '}
              <span className="font-medium text-foreground">PAC &gt; Attributes</span>
            </li>
            <li>
              Click{' '}
              <span className="font-medium text-foreground">"New Attribute"</span>
            </li>
            <li>Select the category (Subject, Resource, Action, or Environment)</li>
            <li>Define the attribute name, data type, and optional description</li>
            <li>Optionally configure a hierarchy (parent-child relationships)</li>
            <li>Save</li>
          </ol>

          <Alert>
            <Lightbulb className="h-4 w-4" />
            <AlertTitle>Tip</AlertTitle>
            <AlertDescription>
              Use dot-notation for attribute names (e.g.,{' '}
              <code className="text-xs bg-muted px-1 py-0.5 rounded">subject.department</code>,{' '}
              <code className="text-xs bg-muted px-1 py-0.5 rounded">resource.sensitivity</code>)
              to keep your policy vocabulary consistent and readable.
            </AlertDescription>
          </Alert>
        </DocSection>

        <DocNav
          prev={{ title: 'XACML Basics', url: '/docs/xacml' }}
          next={{ title: 'Policies', url: '/docs/policies' }}
        />
      </div>
    </>
  )
}
